import { Box, Button, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { LANGUAGE_SWITCH_INTENT_CARD } from '../../utils/constants';
import LanguageSwitch from '../LangaugeSwitch';
import { IntentCard, Intents } from '../../types/intent';
import useGroupedIntents from './useGroupedIntents.hooks';
import useIntentHotkeys from './useIntentHotkeys.hooks';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import PanToolOutlinedIcon from '@mui/icons-material/PanToolOutlined';

// HITL 3.0 bottom-bar grouping of intents. Each intent is placed in a section
// by its `intent` enum value; anything unmapped falls into a trailing "More"
// group so no card is ever silently dropped. `color`/`Icon` theme each section
// header and its outlined pill buttons. The click/dispatch behavior comes from
// this layout's own `useGroupedIntents` hook — independent of the OLD
// IntentsSection so the two can't affect each other.
interface IntentSection {
  label: string;
  intents: Intents[];
  color: string;
  Icon: React.ElementType;
}

const INTENT_SECTIONS: IntentSection[] = [
  {
    label: 'Conversation',
    intents: [
      Intents.greet,
      Intents.acknowledge,
      Intents.askRepeat,
      Intents.askMoment,
    ],
    color: Colors.readBack,
    Icon: ChatBubbleOutlineOutlinedIcon,
  },
  {
    label: 'Order Flow',
    intents: [
      Intents.continue,
      Intents.confirm,
      Intents.readback,
      Intents.complete,
    ],
    color: Colors.welcome,
    Icon: AccountTreeOutlinedIcon,
  },
  {
    label: 'Escalation',
    intents: [Intents.requestStaff],
    color: Colors.torchRed,
    Icon: AutoAwesomeOutlinedIcon,
  },
];

const FALLBACK_SECTION: Omit<IntentSection, 'intents'> = {
  label: 'More',
  color: Colors.silverChalice,
  Icon: ChatBubbleOutlineOutlinedIcon,
};

// The four primaries also have a single-key arrow fast-path (handled in the
// OmniBar). Surfacing the glyph on the pill keeps that otherwise-invisible
// mapping discoverable alongside the Alt+digit badge.
const ARROW_GLYPH: Partial<Record<Intents, string>> = {
  [Intents.acknowledge]: '↑',
  [Intents.askMoment]: '←',
  [Intents.continue]: '→',
  [Intents.readback]: '↓',
};

const sectionMetaFor = (label: string): Omit<IntentSection, 'intents'> =>
  INTENT_SECTIONS.find((section) => section.label === label) ??
  FALLBACK_SECTION;

// Escalation (Request Staff) is pulled to the FRONT of the intent cards so it
// sits directly to the right of the Quick Response input — out of the
// bottom-right corner where it previously stacked under the Cancel / Finish
// Order CTAs (red-on-red, misclick-prone). The remaining, lower-consequence
// groups follow. Derived from the requestStaff intent so a label rename can't
// silently break the ordering.
const ESCALATION_SECTION =
  INTENT_SECTIONS.find((section) =>
    section.intents.includes(Intents.requestStaff)
  )?.label ?? 'Escalation';

const TRAILING_SECTIONS = [
  ...INTENT_SECTIONS.map((section) => section.label).filter(
    (label) => label !== ESCALATION_SECTION
  ),
  FALLBACK_SECTION.label,
];

// Order Flow (Continue / Confirm / Read Back / Complete). Derived from the
// `continue` intent so a label rename can't silently move the OmniBar, which is
// rendered immediately before this section (see render below).
const ORDER_FLOW_SECTION =
  INTENT_SECTIONS.find((section) => section.intents.includes(Intents.continue))
    ?.label ?? 'Order Flow';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    // Fill the bar so the embedded Quick Response input (rendered between the
    // Conversation and Order Flow groups) can take the leftover width; the group
    // cards themselves still hug their buttons.
    flex: '1 1 auto',
    minWidth: 0,
    gap: theme.spacing(1),
    alignItems: 'stretch',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    // Hug the buttons it holds (no min-width / flex-grow) so a single-button
    // group like Escalation doesn't leave trailing space; still shrinkable as a
    // graceful fallback on narrow windows (buttons wrap then).
    backgroundColor: Colors.white,
    border: `${theme.spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 1),
  },
  groupButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignContent: 'flex-start',
    alignItems: 'center',
    flex: 1,
  },
  // Dedicated language-switch card, sibling to the intent group cards. Only
  // rendered for bilingual restaurants. Sized to its content (not flex-grown).
  languageGroup: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexShrink: 0,
    minWidth: 132,
    backgroundColor: Colors.white,
    border: `${theme.spacing(0.125)} solid ${Colors.alto}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1),
  },
  languageSwitch: {
    display: 'flex',
    // Light track so the active (blue) / inactive pills read as a segmented
    // control inside the white card.
    backgroundColor: Colors.wildSand,
    borderRadius: theme.spacing(2),
    padding: '3px',
    height: 34,
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    width: '100%',
  },
  languageOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    borderRadius: theme.spacing(1.75),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 'bold',
    // Title-case the 'english' / 'spanish' values → "English" / "Spanish".
    textTransform: 'capitalize',
    zIndex: 1,
    '&.disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  languageOptionActive: {
    backgroundColor: 'rgb(0, 107, 166)',
    color: 'white',
  },
  languageOptionInactive: {
    backgroundColor: 'transparent',
    color: '#4f5153',
  },
}));

interface IntentsSectionGroupedProps {
  /**
   * Quick Response input (OmniBar) node. Rendered inside the bar immediately
   * before the Order Flow group so it sits between the Conversation primaries
   * and Continue / Confirm / Read Back / Complete. Omit to render the intent
   * groups only.
   */
  omniBar?: React.ReactNode;
}

export default function IntentsSectionGrouped({
  omniBar,
}: IntentsSectionGroupedProps = {}) {
  const classes = useStyles();
  const {
    intents,
    intentsStatus,
    isBilingual,
    isSpanish,
    onIntentDown,
    onIntentUp,
    fireIntentByShortcut,
    shortcutNumberByIdx,
    languageSwitch,
  } = useGroupedIntents();

  // Alt+1…N mirror the on-pill number badges (contiguous over reachable intents).
  useIntentHotkeys(fireIntentByShortcut);

  // Keep each intent's original (filtered) index so click handling and
  // intentsStatus lookups stay identical to the flat layout.
  const intentsWithIdx = intents.map((intent, idx) => ({ intent, idx }));

  const sectionFor = (intent: Intents) =>
    INTENT_SECTIONS.find((section) => section.intents.includes(intent))
      ?.label ?? FALLBACK_SECTION.label;

  const groupedIntents = intentsWithIdx.reduce<
    Record<string, { intent: IntentCard; idx: number }[]>
  >((groups, entry) => {
    const label = sectionFor(entry.intent.intent);
    (groups[label] = groups[label] ?? []).push(entry);
    return groups;
  }, {});

  const renderGroup = (label: string) => {
    const entries = groupedIntents[label];
    if (!entries?.length) return null;
    const meta = sectionMetaFor(label);
    // Escalation (Request Staff) is the one high-consequence control in the bar:
    // a bold red OUTLINE + raised-hand icon + uppercase, deliberately distinct
    // from the tinted conversational pills AND from the solid-red Cancel Order
    // CTA, so the agent presses it with intent.
    const isEscalation = label === ESCALATION_SECTION;
    return (
      <div key={label} className={classes.group}>
        <div className={classes.groupButtons}>
          {entries.map(({ intent: intentCard, idx }) => {
            const clickable = intentsStatus[idx]?.clickable;
            const base = meta.color;
            const textColor = clickable ? base : Colors.silverChalice;
            const bgColor = clickable ? `${base}1F` : Colors.wildSand;
            const borderColor = clickable ? `${base}59` : Colors.alto2;
            const escalationSx = {
              height: 34,
              flex: '0 0 auto',
              minWidth: 64,
              // A touch more room for the uppercase label + icon.
              px: 1.25,
              border: `2px solid ${clickable ? base : Colors.alto2}`,
              borderRadius: '4px',
              backgroundColor: clickable ? Colors.white : Colors.wildSand,
              color: clickable ? base : Colors.silverChalice,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 700,
              fontSize: HITL3_FONT_SIZE,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              '& .MuiButton-startIcon': { marginRight: '4px' },
              '&:hover': {
                backgroundColor: clickable ? `${base}14` : Colors.wildSand,
                border: `2px solid ${clickable ? base : Colors.alto2}`,
              },
              '&.Mui-disabled': {
                backgroundColor: Colors.wildSand,
                color: Colors.silverChalice,
                border: `2px solid ${Colors.alto2}`,
              },
            };
            const pillSx = {
              height: 34,
              // Size to the label (no stretch) — keeps buttons tight.
              flex: '0 0 auto',
              minWidth: 64,
              px: 0.75,
              border: `1px solid ${borderColor}`,
              borderRadius: '4px',
              backgroundColor: bgColor,
              color: textColor,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: HITL3_FONT_SIZE,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: clickable ? `${base}33` : Colors.wildSand,
                border: `1px solid ${borderColor}`,
              },
              '&.Mui-disabled': {
                backgroundColor: Colors.wildSand,
                color: Colors.silverChalice,
                border: `1px solid ${Colors.alto2}`,
              },
            };
            // Alt+number badge: the intent's contiguous shortcut number (Request
            // Staff has none → no badge). Layout is uniform across pills:
            // [number] [label] [optional arrow].
            const shortcutNumber = shortcutNumberByIdx[idx];
            const showBadge = !isEscalation && shortcutNumber !== undefined;
            const arrowGlyph = !isEscalation
              ? ARROW_GLYPH[intentCard.intent]
              : undefined;
            const keyHint = showBadge
              ? `Alt+${shortcutNumber}${arrowGlyph ? ` · ${arrowGlyph}` : ''}`
              : '';
            const keycapSx = {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 15,
              height: 15,
              px: '3px',
              mr: 0.5,
              borderRadius: '3px',
              border: `1px solid ${borderColor}`,
              backgroundColor: Colors.white,
              color: textColor,
              fontSize: '0.62rem',
              fontWeight: 700,
              lineHeight: 1,
            };
            return (
              // Tooltip needs a non-disabled wrapper (the span) to receive hover
              // events while the pill is in its cooldown (disabled) state.
              <Tooltip
                key={`intent-${idx}`}
                title={keyHint}
                arrow
                disableInteractive
              >
                <span>
                  <Button
                    onMouseDown={() => onIntentDown()}
                    onMouseUp={() => onIntentUp(intentCard, idx)}
                    disableElevation
                    disabled={!clickable}
                    startIcon={
                      isEscalation ? (
                        <PanToolOutlinedIcon sx={{ fontSize: 15 }} />
                      ) : undefined
                    }
                    // MUI Button's own emotion styles override makeStyles
                    // classes, so visual styling lives in `sx`.
                    sx={isEscalation ? escalationSx : pillSx}
                  >
                    {showBadge && (
                      <Box component="span" sx={keycapSx}>
                        {shortcutNumber}
                      </Box>
                    )}
                    {intentCard.title}
                    {arrowGlyph && (
                      <Box
                        component="span"
                        sx={{ fontSize: '0.66rem', ml: 0.5, opacity: 0.85 }}
                      >
                        {arrowGlyph}
                      </Box>
                    )}
                  </Button>
                </span>
              </Tooltip>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div id="intentsSection" className={classes.container}>
      {/* Escalation stays leftmost — out of the red-on-red bottom-right corner. */}
      {renderGroup(ESCALATION_SECTION)}
      {isBilingual && (
        <div className={classes.languageGroup}>
          <LanguageSwitch
            classes={classes}
            isSpanish={isSpanish}
            onIntentUp={languageSwitch}
            intentCard={LANGUAGE_SWITCH_INTENT_CARD}
          />
        </div>
      )}
      {TRAILING_SECTIONS.map((label) => (
        <React.Fragment key={label}>
          {/* Quick Response input immediately precedes the Order Flow primaries
              (Continue / Confirm / Read Back / Complete). */}
          {label === ORDER_FLOW_SECTION && omniBar}
          {renderGroup(label)}
        </React.Fragment>
      ))}
    </div>
  );
}
